import React, { useEffect, useState, useRef } from 'react';
import { Modal, Form, Input, AutoComplete, Select, Spin, Row, Col, Button } from 'antd';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../redux/message.reducer';
import { uploadFile } from '../../utils/uploadFile';
import './RepairFormModal.css';

const MAX_IMAGES = 5;

const GIVEN_TO_OPTIONS = [
  { value: 'Dinesh ji', label: 'Dinesh ji' },
  { value: 'Ajit ji', label: 'Ajit ji' },
  { value: 'Subhash ji', label: 'Subhash ji' },
  { value: 'Tarun ji', label: 'Tarun ji' },
  { value: 'Krishna ji', label: 'Krishna ji' },
  { value: 'Bappi ji', label: 'Bappi ji' },
  { value: 'Polish', label: 'Polish' },
  { value: 'Rhodium', label: 'Rhodium' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'inprogress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];

const VALID_STATUSES = ['pending', 'inprogress', 'done'];

function normalizeRepairStatus(s) {
  if (VALID_STATUSES.includes(s)) return s;
  if (s === 'send_to_repair' || s === 'in_progress') return 'inprogress';
  if (s === 'repaired' || s === 'completed') return 'done';
  return 'pending';
}

function RepairPhotoFileInput({ value = [], onChange, disabled }) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const urls = Array.isArray(value) ? value : [];

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0 || disabled) return;

    setUploading(true);
    try {
      let next = [...urls];
      for (const file of files) {
        if (next.length >= MAX_IMAGES) break;
        if (!file.type || !file.type.startsWith('image/')) {
          dispatch(setMessage({ message: 'Only image files are allowed', type: 'warning' }));
          continue;
        }
        const { url } = await uploadFile(file);
        if (url) next = [...next, url].slice(0, MAX_IMAGES);
      }
      onChange?.(next);
    } catch (err) {
      const msg = err.response?.data?.error || 'Upload failed';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (index) => {
    onChange?.(urls.filter((_, i) => i !== index));
  };

  const atMax = urls.length >= MAX_IMAGES;
  const pickLabel = uploading ? 'Uploading…' : atMax ? `${MAX_IMAGES} photos added` : urls.length ? `Add more (${urls.length}/${MAX_IMAGES})` : 'Add photos';

  return (
    <div className="repair-form-photo-block">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="repair-form-file-input-sr-only"
        aria-label="Choose image files"
        disabled={disabled || uploading || atMax}
        onChange={handleFileChange}
      />
      <Button
        type="default"
        size="large"
        icon={<UploadOutlined />}
        loading={uploading}
        disabled={disabled || atMax}
        onClick={() => fileInputRef.current?.click()}
        className="repair-form-photo-btn"
        aria-label="Add photos"
      >
        {pickLabel}
      </Button>
      {urls.length > 0 && (
        <ul className="repair-form-file-input-list" aria-label="Selected photos">
          {urls.map((url, i) => (
            <li key={`${url}-${i}`} className="repair-form-file-input-thumb-item">
              <img src={url} alt="" className="repair-form-file-input-thumb" />
              <button
                type="button"
                className="repair-form-file-input-remove"
                onClick={() => removeAt(i)}
                disabled={disabled || uploading}
                aria-label="Remove image"
              >
                <CloseOutlined />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RepairFormModal({ open, mode, repairId, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      form.resetFields();
      form.setFieldsValue({
        itemImages: [],
        issue: '',
        givenTo: '',
        status: 'pending',
      });
      return;
    }
    if (mode === 'edit' && repairId) {
      let cancelled = false;
      setLoading(true);
      axios
        .get(`/repairs/${repairId}`)
        .then((res) => {
          if (cancelled) return;
          const r = res.data;
          const itemImages = Array.isArray(r.itemImages) ? r.itemImages : [];
          form.setFieldsValue({
            itemImages,
            issue: r.issue ?? '',
            givenTo: r.givenTo ?? '',
            status: normalizeRepairStatus(r.status),
          });
        })
        .catch((err) => {
          if (!cancelled) {
            const msg = err.response?.data?.error || 'Failed to load repair';
            dispatch(setMessage({ message: msg, type: 'error' }));
            onClose?.();
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }
  }, [open, mode, repairId, form, dispatch]);

  const handleSubmit = async (values) => {
    const itemImages = Array.isArray(values.itemImages) ? values.itemImages : [];
    if (itemImages.length > 5) {
      dispatch(setMessage({ message: 'Maximum 5 images allowed', type: 'warning' }));
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        issue: values.issue != null ? String(values.issue).trim() : '',
        itemImages,
        givenTo: (values.givenTo != null && values.givenTo !== '') ? String(values.givenTo).trim() : '',
        status: values.status && VALID_STATUSES.includes(values.status) ? values.status : 'pending',
      };
      if (mode === 'create') {
        await axios.post('/repairs', payload);
        dispatch(setMessage({ message: 'Repair created successfully', type: 'success' }));
      } else {
        await axios.put(`/repairs/${repairId}`, payload);
        dispatch(setMessage({ message: 'Repair updated successfully', type: 'success' }));
      }
      onClose();
      onSuccess?.();
    } catch (err) {
      const msg = err.response?.data?.error || (mode === 'create' ? 'Create repair failed' : 'Update failed');
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setSubmitLoading(false);
    }
  };

  const title = mode === 'create' ? 'New repair' : 'Edit repair';
  const fieldDisabled = loading || submitLoading;

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      destroyOnClose
      className="repair-form-modal"
      maskClosable={!submitLoading}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="repair-form-modal-body" requiredMark={false}>
          <Row gutter={[20, 20]} align="top" wrap className="repair-form-modal-inline-row">
            <Col xs={24} md={24} lg={5} xl={4} className="repair-form-modal-photo">
              <Form.Item
                name="itemImages"
                label="Photos"
                rules={[
                  {
                    validator: (_, value) => {
                      if (Array.isArray(value) && value.length > 5) {
                        return Promise.reject(new Error('Maximum 5 images allowed'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <RepairPhotoFileInput disabled={fieldDisabled} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={9} xl={10}>
              <Form.Item name="issue" label="Remark">
                <Input
                  size="large"
                  placeholder="Short note (max 100 characters)"
                  maxLength={100}
                  showCount={{ formatter: ({ count, maxLength }) => `${count} / ${maxLength}` }}
                  allowClear
                  disabled={fieldDisabled}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={5} xl={5}>
              <Form.Item name="givenTo" label="Given to">
                <AutoComplete
                  size="large"
                  options={GIVEN_TO_OPTIONS}
                  placeholder="Who has the item?"
                  allowClear
                  disabled={fieldDisabled}
                  filterOption={(inputValue, option) =>
                    (option?.value ?? '').toLowerCase().includes((inputValue ?? '').toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={5} xl={5}>
              <Form.Item name="status" label="Status">
                <Select
                  size="large"
                  options={STATUS_OPTIONS}
                  allowClear={false}
                  style={{ width: '100%' }}
                  disabled={fieldDisabled}
                />
              </Form.Item>
            </Col>
          </Row>
          <div className="repair-form-modal-actions">
            <Button onClick={onClose} disabled={submitLoading} size="large" className="app-btn-cancel">
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitLoading} size="large" className="app-btn-primary">
              {mode === 'create' ? 'Create repair' : 'Save changes'}
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
}
